import {
    MdAttachMoney,
    MdBook,
    MdPending,
    MdPeople,
    MdSchool,
    MdSubscriptions,
} from 'react-icons/md';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import DataTable from '../../components/common/DataTable';
import StatCard from '../../components/common/StatCard';
import { COLORS } from '../../utils/constants';
import './Dashboard.css';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useDashboardData } from '../../hooks/useDashboardData';
import TeacherOverview from '../teacherPortal/TeacherOverview';

const Dashboard = () => {
  const { userProfile } = useAuth();
  
  const {
    totalStudents,
    totalTeachers,
    totalLessons,
    activeSubscriptions,
    monthlyRevenue,
    pendingApprovals,
    revenueData,
    subscriptionData,
    studentGrowthData,
    topSubjectsData,
    recentStudentsData,
    recentPaymentsData,
    loading
  } = useDashboardData();

  if (userProfile?.role === 'editor') {
    return <Navigate to="/content-approval" replace />;
  }

  if (userProfile?.role === 'teacher') {
    return <TeacherOverview />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  const stats = [
    {
      title: 'Total Students',
      value: totalStudents.toLocaleString(),
      icon: MdSchool,
      color: COLORS.primary,
    },
    {
      title: 'Total Teachers',
      value: totalTeachers.toLocaleString(),
      icon: MdPeople,
      color: COLORS.success,
    },
    {
      title: 'Total Lessons',
      value: totalLessons.toLocaleString(),
      icon: MdBook,
      color: COLORS.emerald,
    },
    {
      title: 'Active Subscriptions',
      value: activeSubscriptions.toLocaleString(),
      icon: MdSubscriptions,
      color: COLORS.warning,
    },
    {
      title: 'Monthly Revenue',
      value: `₦${monthlyRevenue.toLocaleString()}`,
      icon: MdAttachMoney,
      color: COLORS.success,
    },
    {
      title: 'Pending Approvals',
      value: pendingApprovals.toLocaleString(),
      icon: MdPending,
      color: COLORS.danger,
    },
  ];

  // Recent students table
  const recentStudentsColumns = [
    { header: 'Name', accessor: 'name', width: '25%' },
    { header: 'Class', accessor: 'class', width: '15%' },
    { header: 'Email', accessor: 'email', width: '30%' },
    {
      header: 'Status',
      accessor: 'status',
      width: '15%',
      render: (value) => (
        <span className={`status-badge ${value.toLowerCase()}`}>{value}</span>
      ),
    },
    { header: 'Joined Date', accessor: 'joinedDate', width: '15%' },
  ];



  // Recent payments table
  const recentPaymentsColumns = [
    {
      header: 'Student',
      accessor: 'student',
      width: '28%',
      render: (value, row) => (
        <div>
          <p className="font-bold text-gray-900 text-sm leading-tight">{value || 'Student'}</p>
          {row?.email && (
            <p className="text-xs text-gray-500 font-medium leading-tight mt-0.5">{row.email}</p>
          )}
        </div>
      ),
    },
    {
      header: 'Transaction ID',
      accessor: 'transactionId',
      width: '22%',
      render: (value) => {
        if (!value) return <span className="font-mono text-xs text-gray-400">N/A</span>;
        const displayValue = value.length > 18 
          ? `${value.slice(0, 10)}...${value.slice(-6)}` 
          : value;
        return (
          <span
            className="font-mono text-xs text-gray-700 bg-gray-100/80 px-2 py-0.5 rounded border border-gray-200 whitespace-nowrap inline-block"
            title={value}
          >
            {displayValue}
          </span>
        );
      },
    },
    { header: 'Amount', accessor: 'amount', width: '15%' },
    {
      header: 'Status',
      accessor: 'status',
      width: '15%',
      render: (value) => (
        <span className={`status-badge ${value.toLowerCase()}`}>{value}</span>
      ),
    },
    { header: 'Date', accessor: 'date', width: '20%' },
  ];



  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Welcome back! Here's what's happening with your platform.</p>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* Charts Row 1 */}
      <div className="charts-row">
        {/* Revenue Overview */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>Revenue Overview</h3>
            <select className="chart-filter">
              <option>Last 6 Months</option>
              <option>Last Year</option>
            </select>
          </div>
          <div className="overflow-x-auto">
            <ResponsiveContainer width="100%" height={300} minWidth={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke={COLORS.primary}
                  strokeWidth={3}
                  dot={{ fill: COLORS.primary, r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Subscription Status */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>Subscription Status</h3>
          </div>
          <div className="overflow-x-auto">
            <ResponsiveContainer width="100%" height={300} minWidth={300}>
              <PieChart>
                <Pie
                  data={subscriptionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {subscriptionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="chart-legend">
            {subscriptionData.map((item, index) => (
              <div key={index} className="legend-item">
                <span
                  className="legend-color"
                  style={{ background: item.color }}
                ></span>
                <span className="legend-label">{item.name}</span>
                <span className="legend-value">{item.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="charts-row">
        {/* Student Growth */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>Student Growth</h3>
          </div>
          <div className="overflow-x-auto">
            <ResponsiveContainer width="100%" height={300} minWidth={300}>
              <LineChart data={studentGrowthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="students"
                  stroke={COLORS.success}
                  strokeWidth={3}
                  dot={{ fill: COLORS.success, r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Subjects */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>Top Subjects</h3>
          </div>
          <div className="overflow-x-auto">
            <ResponsiveContainer width="100%" height={300} minWidth={300}>
              <BarChart data={topSubjectsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="subject" stroke="#94a3b8" />
                <YAxis stroke="#94a3b8" />
                <Tooltip />
                <Bar dataKey="students" fill={COLORS.primary} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Tables Row */}
      <div className="tables-row">
        {/* Recent Students */}
        <div className="table-card">
          <div className="table-header">
            <h3>Recent Students</h3>
            <a href="/students" className="view-all-link">
              View All
            </a>
          </div>
          <div className="overflow-x-auto">
            <DataTable
              columns={recentStudentsColumns}
              data={recentStudentsData}
            />
          </div>
        </div>

        {/* Recent Payments */}
        <div className="table-card">
          <div className="table-header">
            <h3>Recent Payments</h3>
            <a href="/payments" className="view-all-link">
              View All
            </a>
          </div>
          <div className="overflow-x-auto">
            <DataTable
              columns={recentPaymentsColumns}
              data={recentPaymentsData}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
